import { t } from "@travily/i18n";
import { tv } from "@ui/lib/tv";
import { useMemo } from "react";
import { applyHousehold, type Costs, type Household } from "../../apply-household";
import { CategoryBreakdown } from "./CategoryBreakdown";
import { CostAttribution } from "./CostAttribution";
import { CostHeadline } from "./CostHeadline";
import { HouseholdControl } from "./HouseholdControl";
import { evidenceSentence } from "./labels";
import { SeasonalityStrip } from "./SeasonalityStrip";

const costsSection = tv({
  slots: {
    root: "flex flex-col gap-6",
    header: "flex flex-col gap-1",
    title: "font-extrabold text-2xl text-neutral-900 tracking-[-0.24px]",
    evidence: "text-neutral-600 text-sm",
    columns: "flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-10",
    main: "flex flex-col gap-6 lg:min-w-0 lg:flex-1",
    side: "lg:w-[320px] lg:shrink-0",
  },
});

export interface CostsSectionProps {
  costs: Costs;
  /** The household the reader has asked for, which leads the server's answer
   *  while a re-quote is in flight. */
  household: Household;
  onHouseholdChange: (household: Household) => void;
  isPending?: boolean;
}

/**
 * What a place costs the family reading about it.
 *
 * While a re-quote is in flight the figures are the client's own mirror of the
 * scaling (apply-household), so the page answers a stepper immediately; the
 * server's reply replaces them, grades and all.
 */
export function CostsSection({ costs, household, isPending = false, onHouseholdChange }: CostsSectionProps) {
  const styles = costsSection();
  const quoted = useMemo(() => (isPending ? applyHousehold(costs, household) : costs), [costs, household, isPending]);

  return (
    <div className={styles.root()}>
      <div className={styles.header()}>
        <h2 className={styles.title()}>{t("components.views.destinations.costs.title")}</h2>
        <p className={styles.evidence()}>{evidenceSentence(quoted.total.grade, quoted.total.coverage)}</p>
      </div>

      <div className={styles.columns()}>
        <div className={styles.main()}>
          <CostHeadline
            attributions={quoted.attributions}
            methodVersion={quoted.method_version}
            pending={isPending}
            total={quoted.total}
          />
          <CategoryBreakdown categories={quoted.categories} />
          {quoted.monthly && <SeasonalityStrip monthly={quoted.monthly} />}
          <CostAttribution attributions={quoted.attributions} />
        </div>

        <div className={styles.side()}>
          <HouseholdControl household={costs.household} isPending={isPending} onCommit={onHouseholdChange} />
        </div>
      </div>
    </div>
  );
}
